import React from 'react';
import { IconButton, ImageViewer, Tag } from '@scalingworks/react-admin-ui';
import { useTranslate } from '@refinedev/core';
import { CiSquareRemove } from 'react-icons/ci';
import { Product } from '~/api';
import { parsePrice } from '~/resources/helpers';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import { Props } from './props';

export const OrderLineProduct: React.FC<Props> = (props) => {
  const { lines = [], lineProducts = [], canModify = false, onDelete } = props;

  // ======================= HOOKS
  const t = useTranslate();

  // ======================= VARIABLES
  const getProduct = (productId: string): Product | undefined => {
    return lineProducts.find((product) => product.id === productId);
  };

  // ======================= VIEWS
  if (isEmpty(lines) || isEmpty(lineProducts)) return null;

  const Divider: React.FC<any> = (props) => (
    <div className={`border border-smoke-100 my-3 ${props.className}`} />
  );
  return (
    <section>
      {lines?.map((line, index) => {
        const {
          id,
          quantity,
          snapshot,
          linePrice,
          linePriceWithAddon,
          productVariant,
          customFields,
        } = line || {};
        const { specialInstructions } = customFields || {};
        const { id: variantId, productId, currencyCode = 'MYR' } = productVariant || {};
        const { variants, assets, featuredAsset } = getProduct(productId) || {};
        const img = featuredAsset?.source || head(assets)?.source || '';
        const { name: variantName } = variants?.find((variant) => variant.id === variantId) || {};

        return (
          <React.Fragment key={id}>
            <div className="flex flex-row items-start w-full">
              <div className="flex flex-row items-start space-x-2">
                <span className="w-8 font-semibold text-lg">{quantity.toString()}×</span>
                {img && (
                  <div className="w-16 flex items-center justify-center">
                    <ImageViewer
                      src={img}
                      className="rounded-lg object-cover !w-14 !h-14"
                      alt="Product"
                    />
                  </div>
                )}
              </div>
              {/* Name */}
              <section className="flex flex-col w-full">
                <div className="flex flex-row items-start justify-between w-full px-2">
                  <h3 className="font-semibold text-lg">{variantName}</h3>

                  {/* Action Panel */}
                  <div className="flex flex-row space-x-1 items-center">
                    <span className="text-right">
                      {currencyCode} {parsePrice(linePriceWithAddon)}
                    </span>
                    {canModify && (
                      <IconButton onClick={() => onDelete?.(id)}>
                        <CiSquareRemove size={30} className="!text-error-300" />
                      </IconButton>
                    )}
                  </div>
                </div>
                {!isEmpty(snapshot?.addons) && (
                  <div className="px-2">
                    {snapshot?.addons?.map(({ group, modifiers }) => {
                      return (
                        <section className="mt-1 mb-2">
                          <div className="flex flex-col">
                            <span className="text-sm text-smoke-600">{group}: </span>
                            {modifiers.map((modifier) => {
                              return modifier.variants.map((modVariant) => {
                                const { name, price, variantId } = modVariant || {};
                                return (
                                  <div
                                    key={name + variantId}
                                    className="flex flex-row items-center justify-between mt-1 pl-2"
                                  >
                                    <span className="text-sm text-smoke-700 bg-smoke-100 px-2 py-0.5 rounded">
                                      {name}
                                    </span>
                                    <span className="text-sm text-smoke-600 text-right">
                                      + {currencyCode} {parsePrice(price)}
                                    </span>
                                  </div>
                                );
                              });
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}

                {specialInstructions && (
                  <section className="my-2 px-2">
                    <span className="text-sm text-smoke-600 font-medium">
                      {t('order.create.instructions', {}, 'Instructions')}
                    </span>
                    <p className="text-sm text-smoke-600 pl-2 mt-1">{specialInstructions}</p>
                  </section>
                )}
              </section>
            </div>
            {index < lines.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </section>
  );
};
